/**
 * Accesos → Grupos → one grupo → **AI** (Plan J, J4 — mockup 4).
 *
 * WHAT THIS TAB SETS, AND WHAT IT ONLY ANSWERS
 * --------------------------------------------
 * Two controls and three answers, and the split is not a design
 * preference — it is what the tree supports, read before it was drawn.
 *
 * The two CONTROLS write `groups.ai_config_json`, and each one has a
 * live reader on the other side:
 *
 *   * **insights** — `ai_snapshot_service.scopes_with_insights_enabled`
 *     checks `ai_config.get("insights_enabled") is True`, and the
 *     watcher fans out over the scopes it returns. It is the cost lever
 *     Pablo accepted as a cost (decisions-log answer 3), which is why
 *     the estimate sits beside the switch and not in a footnote;
 *   * **context note** — `configs/templates.resolve_group_context_note`
 *     (chat, and a mail rendered once per recipient) and
 *     `resolve_scope_context_note` (the insights snapshot, which is
 *     keyed by SCOPE and therefore read by every group that carries
 *     it). It orients the model and grants nothing.
 *
 * The three ANSWERS are places the plan asked for a dial and the
 * product has none. An answer is not a "coming soon" placeholder: it
 * tells the analyst what actually decides the thing they came to
 * change, which is the question a dial would have been standing in for.
 *
 *   * **the chat's reach** — decided by the FILE the connection opens
 *     (`tenancy.paths.current_scope_path`), never by a setting. The
 *     project-wide context the plan calls the "chat scope toggle" is
 *     seeded per PROJECT when it has two or more reports
 *     (`hp_portal/main.py:392`, `report_engine_refresher.py:275`,
 *     `is_cross_report`) and no code anywhere reads a group key to
 *     switch it off. Spec §7 says the group config *may* disable it;
 *     nothing does yet, so there is no switch here to pretend with;
 *   * **the excluded training examples** — `report_training.scope_safe`
 *     exists (portal migration 054) and `configs/templates` filters on
 *     it, but no read endpoint exposes it: `TrainingItemOut` does not
 *     carry the column. So the count is UNKNOWN and renders as unknown.
 *     Never `0` — a zero here would read as "nothing is being withheld
 *     from this group", which is the opposite of the fail-safe default
 *     (`scope_safe DEFAULT FALSE`: the whole existing corpus starts
 *     withheld);
 *   * **the verbosity** — the only length knob the product has is the
 *     group mail's `length` (short · medium · long), and it lives on
 *     the Experiencia tab. A second one here would be a control with no
 *     reader.
 *
 * WHAT A SAVE HERE DOES, AND WHY THE TAB WAS INERT BEFORE
 * -------------------------------------------------------
 * Both controls write `groups.ai_config_json` through the ordinary
 * group PATCH, and both take effect on the readers' NEXT request: the
 * watcher fans out over `scopes_with_insights_enabled` on its next
 * cycle, and every prompt this group's members reach carries the note
 * from `resolve_group_context_note`. Turning insights on asks for one
 * AI Analysis per report per reload under this group's scope, on top of
 * the unrestricted one, billed to the tenant's own OpenAI organisation
 * — which is why the estimate sits beside the switch and not in a
 * footnote.
 *
 * For one slice this tab could not save: `assert_not_j4` refused
 * `ai_config_json` by name with a 400 and `GroupOut` did not carry it
 * back, so `group.ai_config_json` arrived `undefined`, and `undefined`
 * is UNKNOWN — a switch cannot say "off" about a document nobody sent.
 * The tab rendered that honestly, with a `danger` callout and every
 * control inert. It was the right rendering of the wrong state, and the
 * state is what got fixed: the field is now REQUIRED in `GroupOut`, an
 * empty column parses to `insights_enabled: false, context_note: ""`
 * — the portal readers' own conclusion from `{}` — and there is nothing
 * left for a lock to describe.
 *
 * The write is WHOLE-document on the server, so `onSave` sends both
 * keys even when one did not change: a PATCH naming only the lever
 * would clear the note. The optimistic lock is what protects the other
 * direction — a tab left open while somebody else edited earns a 409
 * naming both timestamps rather than overwriting them.
 *
 * THE COST TABLE LIVES HERE TOO, AND IS THE SAME TABLE
 * ---------------------------------------------------
 * `InsightCostTable` is exported and rendered by `AccessAuditPage`
 * (mockup 7) over every project of the tenant, while this tab renders it
 * over the projects this group's scope reaches. One arithmetic, one set
 * of words, one place where «estimación» is spelled — a second copy on
 * the audit page is how two screens start quoting two numbers for one
 * month.
 *
 * And the arithmetic itself is not here either: `aiCostApi.estimate`
 * reads `GET /api/builder/admin/groups/insight-cost/{slug}`, which
 * proxies the portal's estimator. This file decides what the figure
 * MEANS beside a switch — `marginalScopeCost`, and the verb that
 * changes while the number does not — and never what it is.
 */

import { Button, Checkbox, Input, Textarea } from "@omnitok/ui";
import {
  Callout,
  DataTable,
  Section,
  SectionStack,
  StatusChip,
  type Column,
  type StatusDef,
} from "@pervasivemind/admin-ui";
import { Spinner } from "@pervasivemind/bi-components";
import { Calculator } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useT } from "@/i18n";
import {
  ESTIMATE_BASES,
  MAX_CONTEXT_NOTE,
  SCOPE_ALL,
  aiCostApi,
  type GenerationEstimateOut,
  type GroupAiConfig,
  type GroupEffectiveOut,
  type GroupOut,
  type ScopeProjectStatusOut,
} from "@/lib/access-api";
import { DictChip } from "@/pages/admin-access/AccessCells";
import type { TFn } from "@/pages/admin-access/access-status";

// ── the reload cadence, which nothing may guess ──────────────────────

/** `admin_ai_snapshot_estimate`'s own bounds: `ge=1, le=744` (744 = an
 *  hourly reload for 31 days). Mirrored so the box can REPORT an
 *  out-of-range value instead of earning a 422 for it. */
export const RUNS_MIN = 1;
export const RUNS_MAX = 744;

/**
 * Parse the cadence box. `null` when it is not a usable number, and the
 * caller SAYS so rather than substituting anything.
 *
 * There is no default and there must not be one: the watcher generates
 * when the DATA CHANGES, not on a calendar, so how many times a month a
 * project reloads is a fact about the client's loader schedule that only
 * the operator knows — 4 for a weekly reload, ~30 for a nightly one.
 * The server's own parameter is REQUIRED for this reason, in as many
 * words: *"A default here would turn a guess into a figure on a
 * screen."* So an empty box produces no estimate at all, and the screen
 * says what it is waiting for.
 */
export function parseRunsPerMonth(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  if (!Number.isInteger(n) || n < RUNS_MIN || n > RUNS_MAX) return null;
  return n;
}

/** The estimate is shown only beside the cadence it was priced at. A
 *  figure for 4 runs next to a box saying 30 is a wrong number, not a
 *  stale one; until Compute is pressed again there is no figure. */
export function currentEstimate(
  priced: { runs: number; rows: ProjectEstimate[] } | null,
  runsPerMonth: number | null,
): ProjectEstimate[] | null {
  return priced !== null && priced.runs === runsPerMonth ? priced.rows : null;
}

// ── one closed vocabulary, and it has one member ─────────────────────

/**
 * `GenerationEstimateOut.basis`.
 *
 * One member, and it still goes through `resolveStatus` like every other
 * backend word on this surface. The field exists so that *"no screen can
 * render it as a measured figure"*; painting it through a dictionary is
 * what makes the promise mechanical — the day the server answers
 * something this table cannot name, the chip says so in the analyst's
 * face rather than leaving a number whose meaning quietly changed. It is
 * `info` and not `success`: an estimate is neither good news nor bad, it
 * is a claim about a month that has not happened.
 */
export function estimateBasisDict(t: TFn): Record<string, StatusDef> {
  return {
    estimate: { label: t("access.ai.cost.basis.estimate"), tone: "info" },
  };
}

// ── loading the per-project estimate ─────────────────────────────────

/** One project's answer, which is an estimate OR an error, never both
 *  and never neither. A project that could not be estimated is listed
 *  with its error and is EXCLUDED from the total, and the total says it
 *  is partial — a silently smaller sum is how a cost gets under-quoted. */
export interface ProjectEstimate {
  slug: string;
  label: string;
  estimate: GenerationEstimateOut | null;
  error: string | null;
}

export interface ProjectRefLite {
  slug: string;
  label: string;
}

/**
 * Fetch one estimate per project, in parallel, keeping failures.
 *
 * `Promise.all` over `catch`-per-item rather than a bare `Promise.all`:
 * one project whose reports were unpublished must not blank the table
 * for the other eleven. The rejection is kept as text and rendered on
 * its own row.
 */
export async function loadProjectEstimates(
  projects: readonly ProjectRefLite[],
  runsPerMonth: number,
): Promise<ProjectEstimate[]> {
  return Promise.all(
    projects.map(async (p) => {
      try {
        // No `scopes` argument on purpose: omitting it prices the
        // scopes that ACTUALLY earn a snapshot today, and returns their
        // ids — which is what lets the group tab tell "this scope is
        // already being generated" from "turning this on adds one".
        const estimate = await aiCostApi.estimate(p.slug, { runsPerMonth });
        return { slug: p.slug, label: p.label, estimate, error: null };
      } catch (err) {
        return {
          slug: p.slug,
          label: p.label,
          estimate: null,
          error: err instanceof Error ? err.message : String(err),
        };
      }
    }),
  );
}

/** Rows that answered, and the ones that did not. */
export function splitEstimates(rows: readonly ProjectEstimate[]): {
  ok: GenerationEstimateOut[];
  failed: ProjectEstimate[];
} {
  const ok: GenerationEstimateOut[] = [];
  const failed: ProjectEstimate[] = [];
  for (const row of rows) {
    if (row.estimate !== null) ok.push(row.estimate);
    else failed.push(row);
  }
  return { ok, failed };
}

/**
 * What ONE more scope costs across these projects, per month.
 *
 * `reports_with_insights × runs_per_month`, summed. This is the number
 * that belongs beside the switch, and it is deliberately the SAME
 * whether the switch is on or off: it is what this group's scope costs,
 * so off it reads "turning it on adds this" and on it reads "turning it
 * off saves this". Deriving it from today's total instead would collapse
 * to zero exactly when the switch is already on — the moment somebody
 * asks what they would save.
 *
 * It is NOT the delta for a group whose scope is `all`, and the caller
 * has to know that: the unrestricted scope is not a build and not a
 * gate, `insight_scopes_by_project` puts it first unconditionally, and
 * `scopes_with_insights_enabled` filters it out. An unrestricted group's
 * switch therefore adds nothing at all.
 */
export function marginalScopeCost(
  rows: readonly GenerationEstimateOut[],
): number {
  return rows.reduce(
    (sum, r) => sum + r.reports_with_insights * r.runs_per_month,
    0,
  );
}

/** Today's total across the rows that answered. */
export function currentMonthlyCost(
  rows: readonly GenerationEstimateOut[],
): number {
  return rows.reduce((sum, r) => sum + r.generations_per_month, 0);
}

/** Projects where this scope ALREADY earns a snapshot — so its share of
 *  the marginal cost is already being spent, by this group or by another
 *  one carrying the same scope. The set is of SCOPES, not of groups. */
export function projectsAlreadyGenerating(
  rows: readonly ProjectEstimate[],
  scopeId: string,
): string[] {
  return rows
    .filter((r) => r.estimate !== null && r.estimate.scope_ids.includes(scopeId))
    .map((r) => r.label);
}

// ── the cadence box, shared by both screens ──────────────────────────

export function RunsPerMonthField({
  value,
  onChange,
  disabled,
  t,
}: {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  t: TFn;
}): JSX.Element {
  const parsed = parseRunsPerMonth(value);
  const invalid = value.trim() !== "" && parsed === null;
  return (
    <Input
      label={t("access.ai.cost.runs")}
      value={value}
      inputMode="numeric"
      disabled={disabled}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
        onChange(e.target.value)
      }
      helperText={invalid ? undefined : t("access.ai.cost.runsHelp")}
      error={
        invalid
          ? t("access.ai.cost.runsRange", { min: RUNS_MIN, max: RUNS_MAX })
          : undefined
      }
    />
  );
}

// ── the table, rendered by this tab AND by the audit page ────────────

/**
 * Per-project cost of per-scope insights.
 *
 * The word «estimación» is IN the table — its own column, on every row —
 * and not under it. Two of the numbers here are arithmetic about a month
 * that has not happened and one (`measured_last_30_days`) is a plain
 * count of rows that were actually written; putting the qualifier in a
 * footnote is how the three end up read as the same kind of fact.
 */
export function InsightCostTable({
  rows,
  emptyLabel,
  t,
}: {
  rows: readonly ProjectEstimate[];
  emptyLabel: string;
  /** Injected, not taken from `useT()`, so a test can echo the keys and
   *  assert on STRUCTURE instead of on copy — the pattern
   *  `EffectiveScopeCard` already uses for the same reason. */
  t: TFn;
}): JSX.Element {
  const bases = estimateBasisDict(t);

  const columns: Column<ProjectEstimate>[] = [
    {
      id: "project",
      header: t("access.ai.cost.col.project"),
      cell: (row) => (
        <div className="min-w-0">
          <div className="truncate font-medium text-neutral-900">
            {row.label}
          </div>
          <div className="truncate font-mono text-[13px] text-neutral-500">
            {row.slug}
          </div>
        </div>
      ),
    },
    {
      id: "reports",
      header: t("access.ai.cost.col.reports"),
      align: "right",
      width: "w-32",
      cell: (row) =>
        row.estimate === null ? (
          <Unavailable t={t} />
        ) : (
          <span className="text-neutral-900">
            {row.estimate.reports_with_insights}
          </span>
        ),
    },
    {
      id: "scopes",
      header: t("access.ai.cost.col.scopes"),
      width: "w-56",
      cell: (row) =>
        row.estimate === null ? (
          <Unavailable t={t} />
        ) : (
          <div className="min-w-0">
            <span className="text-neutral-900">{row.estimate.scopes}</span>
            <span className="block truncate font-mono text-[13px] text-neutral-500">
              {row.estimate.scope_ids.join(" · ")}
            </span>
          </div>
        ),
    },
    {
      id: "estimate",
      header: t("access.ai.cost.col.perMonth"),
      align: "right",
      width: "w-56",
      cell: (row) =>
        row.estimate === null ? (
          <Unavailable t={t} />
        ) : (
          <div className="flex items-center justify-end gap-2">
            <span className="text-neutral-900">
              {row.estimate.generations_per_month}
            </span>
            <DictChip value={row.estimate.basis} dictionary={bases} t={t} />
          </div>
        ),
    },
    {
      id: "measured",
      header: t("access.ai.cost.col.measured"),
      align: "right",
      width: "w-44",
      cell: (row) =>
        row.estimate === null ? (
          <Unavailable t={t} />
        ) : (
          <span className="text-neutral-900">
            {row.estimate.measured_last_30_days}
          </span>
        ),
    },
  ];

  return (
    <>
      <DataTable
        columns={columns}
        rows={[...rows]}
        rowKey={(row) => row.slug}
        caption={t("access.ai.cost.title")}
        empty={<p className="text-sm text-neutral-600">{emptyLabel}</p>}
      />
      {rows
        .filter((r) => r.error !== null)
        .map((r) => (
          <Callout
            key={r.slug}
            tone="danger"
            title={t("access.ai.cost.failedTitle", { project: r.label })}
          >
            <span className="whitespace-pre-wrap">{r.error}</span>
          </Callout>
        ))}
    </>
  );
}

/**
 * The sentence beside the switch: what this group's scope costs a month.
 *
 * ITS WHOLE POINT IS THAT IT DOES NOT DEPEND ON THE SWITCH
 * -------------------------------------------------------
 * `insightsOn` changes the VERB and nothing else. The figure is
 * :func:`marginalScopeCost` — one more scope over these projects — so
 * off it reads "turning it on adds this" and on it reads "turning it off
 * saves this", and both are the same true number.
 *
 * The obvious alternative is a trap and the reason this is a component
 * with a test rather than three lines inline: deriving the delta from
 * TODAY's generating set (the projects where this scope is not yet in
 * `scope_ids`) gives the right answer while the switch is off and
 * collapses to ZERO the moment it is on — which is precisely the moment
 * somebody asks what turning it off would save. `__tests__/group-tabs`
 * pins both directions.
 *
 * `t` is injected rather than taken from `useT()` so that test can echo
 * the keys and assert on structure instead of on copy.
 */
export function InsightCostSummary({
  rows,
  insightsOn,
  scopeId,
  emptyLabel,
  t,
}: {
  rows: readonly ProjectEstimate[];
  insightsOn: boolean;
  /** This group's scope, to say where its share is ALREADY being spent.
   *  The generating set is of SCOPES, not of groups, so another group
   *  carrying the same scope is paying for it too. */
  scopeId: string;
  emptyLabel: string;
  t: TFn;
}): JSX.Element {
  const { ok, failed } = splitEstimates(rows);
  const marginal = marginalScopeCost(ok);
  const already = projectsAlreadyGenerating(rows, scopeId);
  return (
    <div className="mt-3 space-y-2">
      {rows.length === 0 ? (
        // Not «adds about 0». Zero is arithmetically true here and reads
        // as "free, switch it on", when the fact is that this scope
        // reaches no project at all. The reason replaces the figure
        // rather than sitting under it.
        <p className="text-sm text-neutral-700">{emptyLabel}</p>
      ) : (
        <p className="text-sm text-neutral-900">
          {insightsOn
            ? t("access.ai.cost.saves", { n: marginal })
            : t("access.ai.cost.adds", { n: marginal })}{" "}
          <DictChip
            value={ESTIMATE_BASES[0]}
            dictionary={estimateBasisDict(t)}
            t={t}
          />
        </p>
      )}
      {failed.length > 0 && (
        <p className="text-[13px] text-neutral-700">
          {t("access.ai.cost.partial", { n: failed.length })}
        </p>
      )}
      {already.length > 0 && (
        <p className="text-[13px] text-neutral-700">
          {t("access.ai.cost.alreadyGenerating", {
            projects: already.join(", "),
          })}
        </p>
      )}
      <InsightCostTable rows={rows} emptyLabel={emptyLabel} t={t} />
    </div>
  );
}

/** A cell for a project whose estimate did not come back. Never a zero:
 *  on a cost table a zero is a real and different answer. */
function Unavailable({ t }: { t: TFn }): JSX.Element {
  return (
    <StatusChip
      label={t("access.ai.cost.unavailable")}
      tone="warning"
      title={t("access.ai.cost.unavailableHint")}
    />
  );
}

// ── the save payload ─────────────────────────────────────────────────

/**
 * What the Save button sends. BOTH keys, always.
 *
 * The server writes `ai_config_json` WHOLE — `parse_ai_config` returns
 * the model's own dump, not a merge — so a PATCH naming only
 * `insights_enabled` clears `context_note`. Sending the half that
 * changed would therefore delete a group's context note the first time
 * somebody flipped the switch alone, silently, and the note reaches
 * every prompt this group's members touch.
 *
 * It is a function rather than an object literal inline so that
 * property has a seam a test can hold: `__tests__/group-tabs` asserts
 * the payload carries an UNCHANGED value too.
 */
export function aiConfigToSave(
  insights: boolean,
  note: string,
): GroupAiConfig {
  return { insights_enabled: insights, context_note: note };
}

// ── the tab ──────────────────────────────────────────────────────────

/** The (scope, project) states that mean a FILE exists — `built` and
 *  `no_mapping`. Mirrors `cli._OUTCOME_HAS_FILE`, and `no_mapping` is in
 *  it because it PUBLISHED a file, an empty one: an empty dashboard, not
 *  a 503. A project where the scope has no file cannot be narrated under
 *  it, so it costs nothing and is not in the table. */
const HAS_FILE = new Set(["built", "no_mapping"]);

export function projectsWithAFile(
  projects: readonly ScopeProjectStatusOut[],
): ProjectRefLite[] {
  return projects
    .filter((p) => HAS_FILE.has(p.state))
    .map((p) => ({ slug: p.project_slug, label: p.project_name }));
}

export function GroupAiTab({
  group,
  effective,
  saving,
  onSave,
}: {
  group: GroupOut;
  /** `GET /{id}/effective` — where this group's scope has a file. Null
   *  while it is still loading; the cost half waits rather than pricing
   *  an empty project list as zero. */
  effective: GroupEffectiveOut | null;
  saving: boolean;
  onSave: (ai: GroupAiConfig) => Promise<void>;
}): JSX.Element {
  const { t } = useT();

  // Always present: `GroupOut.ai_config_json` is required, and an empty
  // column parses to «off, no note» — which is what the two portal
  // readers conclude from `{}` — rather than to an absence a control
  // would have to render as unknown.
  const config = group.ai_config_json;

  const [note, setNote] = useState(config.context_note);
  const [insights, setInsights] = useState(config.insights_enabled);
  const [runs, setRuns] = useState("");
  const [priced, setPriced] = useState<{ runs: number; rows: ProjectEstimate[] } | null>(
    null,
  );
  const [pricing, setPricing] = useState(false);

  // Re-seed the form when the group is reloaded (a save, a tab switch).
  // Keyed on the stored values so a keystroke is never stomped by a
  // re-render that changed nothing.
  useEffect(() => {
    setNote(config.context_note);
    setInsights(config.insights_enabled);
  }, [config.context_note, config.insights_enabled]);

  const applicable = useMemo(
    () => (effective === null ? null : projectsWithAFile(effective.projects)),
    [effective],
  );

  const runsPerMonth = parseRunsPerMonth(runs);
  const rows = currentEstimate(priced, runsPerMonth);

  const price = useCallback(async (): Promise<void> => {
    if (runsPerMonth === null || applicable === null) return;
    const at = runsPerMonth;
    setPricing(true);
    try {
      setPriced({ runs: at, rows: await loadProjectEstimates(applicable, at) });
    } finally {
      setPricing(false);
    }
  }, [runsPerMonth, applicable]);

  const unrestricted = group.scope_id === SCOPE_ALL;

  const dirty =
    note !== config.context_note || insights !== config.insights_enabled;

  /**
   * Over the server's bound, reported rather than clipped.
   *
   * No `maxLength` on the box: that silently swallows the tail of a
   * paste, and this field is prose somebody wrote elsewhere. The bound
   * is a PROMPT bound — the note is appended verbatim to every prompt
   * every member reaches — so the honest failure is to say the length
   * and refuse to send, which is the same shape `RunsPerMonthField`
   * uses one section up. The server refuses it too, with a 400 naming
   * the field; this only means the analyst learns it before the round
   * trip.
   */
  const noteTooLong = note.length > MAX_CONTEXT_NOTE;

  return (
    <SectionStack>
      {/* ── insights, with what it costs, before it is flipped ── */}
      <Section
        title={t("access.ai.insights.title")}
        description={t("access.ai.insights.desc")}
      >
        <Checkbox
          toggle
          label={t("access.ai.insights.switch")}
          description={t("access.ai.insights.switchHelp")}
          checked={insights}
          disabled={saving}
          onCheckedChange={setInsights}
        />

        <div className="mt-4 rounded-md border border-neutral-200 p-3">
          <p className="text-sm font-semibold text-neutral-900">
            {t("access.ai.cost.beforeTitle")}
          </p>
          <p className="mt-1 text-[13px] text-neutral-600">
            {unrestricted
              ? t("access.ai.cost.unrestricted")
              : t("access.ai.cost.beforeBody")}
          </p>

          {!unrestricted && (
            <>
              <div className="mt-3 flex flex-wrap items-end gap-2">
                <RunsPerMonthField
                  value={runs}
                  onChange={setRuns}
                  disabled={applicable === null}
                  t={t}
                />
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<Calculator size={14} />}
                  disabled={runsPerMonth === null || applicable === null || pricing}
                  onClick={() => void price()}
                >
                  {t("access.ai.cost.compute")}
                </Button>
                {pricing && (
                  <span className="inline-flex items-center gap-2 pb-2 text-sm text-neutral-600">
                    <Spinner size={14} ariaLabel={t("common.loading")} />
                    {t("common.loading")}
                  </span>
                )}
              </div>

              {rows === null ? (
                <p className="mt-3 text-[13px] text-neutral-600">
                  {t("access.ai.cost.notYet")}
                </p>
              ) : (
                <InsightCostSummary
                  rows={rows}
                  insightsOn={insights}
                  scopeId={group.scope_id}
                  emptyLabel={t("access.ai.cost.noProjects")}
                  t={t}
                />
              )}
            </>
          )}
        </div>
      </Section>

      {/* ── the note ── */}
      <Section
        title={t("access.ai.note.title")}
        description={t("access.ai.note.desc")}
      >
        <Textarea
          label={t("access.ai.note.label")}
          value={note}
          rows={5}
          fullWidth
          disabled={saving}
          helperText={noteTooLong ? undefined : t("access.ai.note.help")}
          error={
            noteTooLong
              ? t("access.ai.note.tooLong", {
                  n: note.length,
                  max: MAX_CONTEXT_NOTE,
                })
              : undefined
          }
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
            setNote(e.target.value)
          }
        />
        <Callout tone="info" title={t("access.ai.note.sharedTitle")}>
          {t("access.ai.note.shared")}
        </Callout>
      </Section>

      <div className="flex flex-wrap items-center justify-end gap-3">
        <p className="mr-auto text-[13px] text-neutral-600">
          {t("access.ai.saveEffect")}
        </p>
        <Button
          variant="primary"
          size="sm"
          disabled={!dirty || saving || noteTooLong}
          onClick={() =>
            // BOTH keys, always — see `aiConfigToSave`.
            void onSave(aiConfigToSave(insights, note))
          }
        >
          {t("access.ai.save")}
        </Button>
      </div>

      {/* ── the three answers ── */}
      <Section
        title={t("access.ai.reach.title")}
        description={t("access.ai.reach.desc")}
      >
        <dl className="grid gap-3 sm:grid-cols-2">
          <div>
            <dt className="text-[13px] font-semibold text-neutral-600">
              {t("access.groups.form.scope")}
            </dt>
            <dd className="text-sm text-neutral-900">
              {unrestricted ? t("access.scopes.all") : group.scope_name}{" "}
              <span className="font-mono text-[13px] text-neutral-500">
                {group.scope_id}
              </span>
            </dd>
          </div>
          <div>
            <dt className="text-[13px] font-semibold text-neutral-600">
              {t("access.ai.reach.projectWide")}
            </dt>
            <dd className="text-sm text-neutral-900">
              {t("access.ai.reach.projectWideBody")}
            </dd>
          </div>
        </dl>
      </Section>

      <Section
        title={t("access.ai.examples.title")}
        description={t("access.ai.examples.desc")}
      >
        <div className="flex items-center gap-3">
          <StatusChip
            label={t("access.ai.examples.unknown")}
            tone="warning"
            title={t("access.ai.examples.unknownHint")}
          />
          <p className="text-[13px] text-neutral-700">
            {t("access.ai.examples.body")}
          </p>
        </div>
      </Section>

      <Section
        title={t("access.ai.verbosity.title")}
        description={t("access.ai.verbosity.desc")}
      >
        <p className="text-sm text-neutral-700">
          {t("access.ai.verbosity.body")}
        </p>
      </Section>
    </SectionStack>
  );
}
